import { isCaponeTenant } from "@capone/common";
import { Box, Typography } from "@material-ui/core";
import {
  B2BLoadingPopup,
  Header,
  HotelSearchLoadingImage,
  HotelSplitMapView,
  useDeviceTypes,
  WishlistHeaderEntry,
  wishlistManagementModalActions,
} from "halifax";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { RouterProps, useLocation } from "react-router";

import clsx from "clsx";

import { ClientContext } from "../../App";
import { PATH_HOME, PATH_HOME_STAYS } from "../../utils/paths";

import {
  useExperiments,
  getExperimentVariant,
  AVAILABLE,
  TRAVEL_WALLET_CREDITS_EXPERIMENT,
  TRAVEL_CREDIT_HISTORY_EXPERIMENT,
  getExperimentVariantCustomVariants,
  STAYS_SEARCH,
  FTC_PRICING_UPDATE,
  FTC_PRICING_UPDATE_VARIANTS,
  STAYS_WISHLIST,
} from "../../context/experiments";
import { config } from "../../api/config";
import { RewardsAccountSelection } from "../rewards/components";
import { TravelWalletDrawer } from "../travel-wallet/components";
import {
  AvailabilityErrorModal,
  AvailabilityList,
} from "../availability/components";
import { WishlistAvailabilityConnectorProps } from "./container";
import { HotelAvailabilityCallState } from "../availability/reducer/state";
import "../availability/styles.scss";
import * as constants from "../availability/textConstants";
import { ConnectedAvailabilityClusterMap } from "../availability/components/AvailabilityClusterMap/container";
import {
  useGetAllProductIdsByUserQuery as getAllWishlistedProductIds,
  useGetWishlistByIdQuery,
} from "@capone/common";
import dayjs from "dayjs";
import { ConnectedMobileWishlistAvailability } from "./components/MobileWishlistAvailability/container";
import { useDispatch } from "react-redux";
import { ConnectedWishlistManagementModal } from "./components/WishlistManagementModal/container";
import { WishlistSnackbar } from "./components/WishlistSnackbar/component";

export interface IWishlistAvailability
  extends WishlistAvailabilityConnectorProps,
    RouterProps {}

export const WishlistAvailability = (props: IWishlistAvailability) => {
  const {
    hotelAvailabilityCallState,
    fetchWishlistAvailability,
    history,
    fetchTravelWalletDetails,
    largestValueAccount,
    fetchTravelWalletCreditHistory,
    listPaymentMethods,
    setFTCPricingUpdateVariant,
  } = props;

  const { logo } = useContext(ClientContext);
  const { matchesMobile } = useDeviceTypes();
  const location = useLocation();
  const dispatch = useDispatch();
  const searchParams = new URLSearchParams(location.search);

  // If search term is location (ex: Toronto) vs if search term is point of interest (ex: Toronto Zoo)
  const [mobileMapOpen, setMobileMapOpen] = useState(false);
  const [wishlistId, setWishlistId] = useState<string | undefined>(undefined);

  const expState = useExperiments();

  const travelWalletCreditsExperiment = getExperimentVariant(
    expState.experiments,
    TRAVEL_WALLET_CREDITS_EXPERIMENT
  );

  const staysSearchEnabled =
    getExperimentVariant(expState.experiments, STAYS_SEARCH) === AVAILABLE;

  const isWishlistExperiment =
    getExperimentVariant(expState.experiments, STAYS_WISHLIST) === AVAILABLE;

  const isTravelWalletCreditsExperiment = React.useMemo(
    () => travelWalletCreditsExperiment === AVAILABLE,
    [travelWalletCreditsExperiment]
  );

  const showEarnEnhancement =
    isCaponeTenant(config.TENANT) &&
    !!largestValueAccount &&
    !!largestValueAccount.earn.hotelsMultiplier;

  const travelCreditHistoryExperiment = getExperimentVariant(
    expState.experiments,
    TRAVEL_CREDIT_HISTORY_EXPERIMENT
  );
  const isTravelCreditHistoryExperiment = useMemo(() => {
    return travelCreditHistoryExperiment === AVAILABLE;
  }, [travelCreditHistoryExperiment]);

  useEffect(() => {
    const id = searchParams.get("id");

    fetchTravelWalletDetails();
    listPaymentMethods();

    if (id) {
      setWishlistId(id);
    }
  }, []);

  getAllWishlistedProductIds();

  const { data: { wishlist } = {} } = useGetWishlistByIdQuery(
    { wishlistId: { value: wishlistId ?? "" } },
    {
      skip: !wishlistId, // Skip until wishlistId is parsed from the URL
    }
  );

  useEffect(() => {
    if (wishlist) {
      fetchWishlistAvailability({
        history,
        lodgingIds: wishlist.items.map((item) => item.product.id.value),
        stayDates: {
          fromDate: wishlist.wishlistData?.checkInDate || dayjs().toDate(),
          untilDate:
            wishlist.wishlistData?.checkOutDate ||
            dayjs().add(7, "day").toDate(),
        },
        guests: {
          numAdults: wishlist.wishlistData?.adults || 2,
          numChildren: wishlist.wishlistData?.children || 0,
          petsIncluded: wishlist.wishlistData?.petsIncluded || false,
        },
      });
    }
  }, [wishlist]);

  useEffect(() => {
    if (isTravelCreditHistoryExperiment) {
      fetchTravelWalletCreditHistory();
    }
  }, [isTravelCreditHistoryExperiment]);

  const ftcPricingUpdateVariant = getExperimentVariantCustomVariants(
    expState.experiments,
    FTC_PRICING_UPDATE,
    FTC_PRICING_UPDATE_VARIANTS
  );

  useEffect(() => {
    setFTCPricingUpdateVariant(ftcPricingUpdateVariant);
  }, [ftcPricingUpdateVariant]);

  const renderDesktopView = () => (
    <HotelSplitMapView
      className={clsx("hotel-availability-container", config.TENANT)}
      header={
        <Header
          className="rewards-components-section"
          left={
            <Box className={"rewards-account-section-left-content"}>
              <Box
                className={"logo"}
                onClick={() =>
                  history.push(staysSearchEnabled ? PATH_HOME_STAYS : PATH_HOME)
                }
              >
                {logo}
              </Box>
              <Box className={"rewards-account-section-travel-details"}>
                <Typography variant={"body1"} tabIndex={0}>
                  {wishlist?.wishlistData?.name}
                </Typography>
                <Typography variant={"body2"}>
                  ({wishlist?.items?.length}) stays saved
                </Typography>
              </Box>
            </Box>
          }
          right={
            <Box className="desktop-hotel-availability-rewards-account-contents">
              <RewardsAccountSelection
                className={clsx("b2b", {
                  "hide-balance-border": isTravelWalletCreditsExperiment,
                })}
                popoverClassName="b2b"
              />

              {isWishlistExperiment && (
                <WishlistHeaderEntry
                  onClick={() =>
                    dispatch(wishlistManagementModalActions.openModal({}))
                  }
                />
              )}

              {isTravelWalletCreditsExperiment && <TravelWalletDrawer />}
            </Box>
          }
        />
      }
      leftClassName={clsx("list-section")}
      left={<AvailabilityList isWishlistMode={true} />}
      rightClassName={clsx("map-section")}
      right={<ConnectedAvailabilityClusterMap isWishlistMode />}
    />
  );

  const renderMobileView = () => (
    <Box
      className={clsx("hotel-availability-container", config.TENANT, {
        "mobile-map": mobileMapOpen,
      })}
    >
      {wishlist && (
        <ConnectedMobileWishlistAvailability
          onShowMap={setMobileMapOpen}
          wishlist={wishlist}
        />
      )}
    </Box>
  );

  const capOneLoadingSecondaryText = showEarnEnhancement
    ? constants.EARN_ENHANCEMENT_SUBTITLE(
        largestValueAccount.earn.hotelsMultiplier,
        staysSearchEnabled ? largestValueAccount.earn.homesMultiplier : "",
        largestValueAccount.productDisplayName,
        largestValueAccount.rewardsBalance.currencyDescription ??
          largestValueAccount.rewardsBalance.currency
      )
    : largestValueAccount?.productDisplayName.toLowerCase().includes("paradise")
    ? constants.PARADISE_SUBTITLE
    : constants.fetchingAvailabilitySecondaryText(staysSearchEnabled);

  return (
    <>
      {hotelAvailabilityCallState ===
        HotelAvailabilityCallState.InitialSearchCallInProcess ||
      (matchesMobile &&
        hotelAvailabilityCallState ==
          HotelAvailabilityCallState.InitialMapSearchCallInProcess) ? (
        <B2BLoadingPopup
          open={
            hotelAvailabilityCallState ===
              HotelAvailabilityCallState.InitialSearchCallInProcess ||
            (matchesMobile &&
              hotelAvailabilityCallState ==
                HotelAvailabilityCallState.InitialMapSearchCallInProcess)
          }
          message={""}
          secondaryMessage={capOneLoadingSecondaryText}
          image={HotelSearchLoadingImage}
          className="hotel-search-loading-popup"
          popupSize={matchesMobile ? "mobile" : "desktop"}
        />
      ) : null}
      <Box
        className={clsx("hotel-availability-root", {
          "full-width": !matchesMobile,
        })}
      >
        {matchesMobile ? renderMobileView() : renderDesktopView()}
      </Box>

      <ConnectedWishlistManagementModal />
      <WishlistSnackbar />
      <AvailabilityErrorModal />
    </>
  );
};
